import React, {
  FunctionComponent, useEffect, useState, Fragment
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import i18n from 'i18n';
import { useTranslation } from 'react-i18next';
import {
  Classes, Dialog, ProgressBar
} from '@blueprintjs/core';
import { SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import {
  JurisdictionAreaGridData,
  JurisdictionAreaPrivileges
} from 'data/jurisdictionArea/jurisdictionArea.types';
import { GridSettings } from 'data/types';
import * as selectors from 'data/jurisdictionArea/jurisdictionArea.selectors';
import * as afsSelectors from 'data/accessFunction/accessFunction.selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/jurisdictionArea/jurisdictionArea.actions';
import * as afsActions from 'data/accessFunction/accessFunction.actions';
import * as userActions from 'users/users-actions';
import { AppToaster, showSuccessMessage, showError } from 'shared/toaster';
import { adaptJurisdictionAreaListToGrid } from 'data/jurisdictionArea/jurisdictionArea.adapter';
import JurisdictionAreaGrid from '../../components/JurisdictionAreaGrid';
import JurisdictionAreaDialog from '../../components/JurisdictionAreaDialog';

import {
  jurisdictionAreaAutoGroupColumnDef,
  jurisdictionAreaColumnDefs,
  jurisdictionAreaColumnDefsVersion
} from '../../jurisdictionArea.types';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  userName: authenticationSelectors.getAuthenticationUserName(state),
  privileges: authenticationSelectors.getAuthenticationPrivileges(state),
  beforeLogout: authenticationSelectors.getAuthenticationBeforeLogout(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  data: selectors.getJurisdictionAreaList(state),
  accessFunctionsList: afsSelectors.getAccessFunctions(state),
  selectedJurisdictionArea: selectors.getSelectedJurisdictionArea(state)
});

const mapDispatch = {
  getJurisdictionAreaList: actions.getJurisdictionAreaList,
  getAccessFunctions: afsActions.getAccessFunctionsWithViewOptions,
  goToJurisdictionAreaCreate: actions.goToJurisdictionAreaCreate,
  goToJurisdictionAreaEdit: actions.goToJurisdictionAreaEdit,
  selectJurisdictionArea: actions.selectJurisdictionArea,
  deleteJurisdictionArea: actions.deleteJurisdictionArea,
  getGridSettings: userActions.getUserSettings,
  saveGridSettings: userActions.putUserSettings,
  goToJurisdictionAreaView: actions.goToJurisdictionAreaView,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

const USER_CATEGORY_SETTINGS = 'JurisdictionAreaSettings';

export const JurisdictionAreaList: FunctionComponent<Props> = ({
  userName,
  history,
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data,
  accessFunctionsList,
  selectedJurisdictionArea,
  goToJurisdictionAreaCreate,
  goToJurisdictionAreaEdit,
  goToJurisdictionAreaView,
  selectJurisdictionArea,
  getJurisdictionAreaList,
  getAccessFunctions,
  deleteJurisdictionArea,
  getGridSettings,
  saveGridSettings,
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [gridData, setGridData] = useState<JurisdictionAreaGridData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progressBarValue, setProgressBarValue] = useState(0);
  const [accessFunctionPending, setAccessFunctionPending] = useState<string>('');
  const [failedAccessFunctions, setFailedAccessFunctions] = useState<string[]>([]);
  const [isJurisdictionAreaDialogOpen, setIsJurisdictionAreaDialogOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    getJurisdictionAreaList(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);

    return () => {
      AppToaster.clear();
    };
  }, [deliveryFunctionId, getJurisdictionAreaList, getAccessFunctions]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
    const adaptedGridData = adaptJurisdictionAreaListToGrid(data, accessFunctionsList);
    setGridData(adaptedGridData);

    return () => {
      AppToaster.clear();
    };
  }, [data, accessFunctionsList]);

  const handleRefresh = () => {
    getJurisdictionAreaList(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
  }

  const handleCreateNew = () => goToJurisdictionAreaCreate(history);

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedRows: JurisdictionAreaGridData[] = event.api.getSelectedRows();
    const selectedJurisdictionAreaId = selectedJurisdictionArea.length ? selectedJurisdictionArea[0].id : '';
    const selectedRowGroupId = selectedRows.length ? selectedRows[0].id : '';
    if (selectedJurisdictionAreaId !== selectedRowGroupId) {
      selectJurisdictionArea(selectedRows as JurisdictionAreaGridData[]);
    }
  };

  const handleGetGridSettings = () =>
    getGridSettings(USER_CATEGORY_SETTINGS, userName);

  const handleSaveGridSettings = (gridSettings: GridSettings) =>
    saveGridSettings(USER_CATEGORY_SETTINGS, userName, gridSettings);

  const handleGoToJurisdictionAreaView = () => { if (selectedJurisdictionArea.length) { goToJurisdictionAreaView(history); } }

  const handleJurisdictionAreaEdit = () => { if (selectedJurisdictionArea.length) { goToJurisdictionAreaEdit(history); } }

  const handleJurisdictionAreaDelete = () => {
    if (selectedJurisdictionArea.length) {
      let numAccessFunctionsHandled = 0;
      const accessFunctionsFailedToBeDeleted: string[] = [];
      setIsLoading(true);
      setAccessFunctionPending(selectedJurisdictionArea[0].AFNAME);
      setFailedAccessFunctions([]);
      setProgressBarValue(0);
      let allAccessFunctionsDeleted = true;

      selectedJurisdictionArea.map(async (jurisdictionAreaToDelete, index) => {
        const res = await deleteJurisdictionArea(deliveryFunctionId, jurisdictionAreaToDelete.id);
        if (res === undefined) {
          allAccessFunctionsDeleted = false;
          accessFunctionsFailedToBeDeleted.push(`${jurisdictionAreaToDelete.AFNAME} (${jurisdictionAreaToDelete.AFTYPE})`);
        }
        if (index === selectedJurisdictionArea.length - 1) {
          setIsLoading(false);
          if (allAccessFunctionsDeleted || (accessFunctionsFailedToBeDeleted.length < selectedJurisdictionArea.length)) {
            if (accessFunctionsFailedToBeDeleted.length) {
              setFailedAccessFunctions(accessFunctionsFailedToBeDeleted);
              setIsJurisdictionAreaDialogOpen(true);
            } else {
              getJurisdictionAreaList(deliveryFunctionId);
              showSuccessMessage(i18n.t('jurisdictionArea.delete.success', { areaName: jurisdictionAreaToDelete.SS8ID }), 5000);
            }
          } else {
            getJurisdictionAreaList(deliveryFunctionId);
            showError(i18n.t('jurisdictionArea.delete.failed', { areaName: jurisdictionAreaToDelete.SS8ID, error: 'Error' }));
          }
          return;
        }
        numAccessFunctionsHandled += 1;
        setProgressBarValue(numAccessFunctionsHandled / selectedJurisdictionArea.length);
        setAccessFunctionPending(selectedJurisdictionArea[index + 1].AFNAME);
      });
    }
  };

  const closeJurisdictionAreaDialog = () => {
    setIsJurisdictionAreaDialogOpen(false);
    getJurisdictionAreaList(deliveryFunctionId);
    const jurisdictionAreaName = selectedJurisdictionArea[0].SS8ID;
    showSuccessMessage(i18n.t('jurisdictionArea.delete.success', { areaName: jurisdictionAreaName }), 5000);
  }

  const hasPrivilegesToCreate = privileges.includes(
    JurisdictionAreaPrivileges.Edit
  );
  const hasPrivilegeToDelete = privileges.includes(
    JurisdictionAreaPrivileges.Delete
  );

  return (
    <Fragment>
      <JurisdictionAreaGrid
        selectedJurisdictionArea={selectedJurisdictionArea?.[0]}
        data={gridData}
        columnDefs={jurisdictionAreaColumnDefs}
        columnDefsVersion={jurisdictionAreaColumnDefsVersion}
        autoGroupColumnDef={jurisdictionAreaAutoGroupColumnDef}
        noResults={false}
        beforeLogout={beforeLogout}
        lastUpdatedAt={lastUpdatedAt}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onGetGridSettings={handleGetGridSettings}
        onSaveGridSettings={handleSaveGridSettings}
        onSelectionChanged={handleSelectionChanged}
        onEdit={handleJurisdictionAreaEdit}
        onDelete={handleJurisdictionAreaDelete}
        onSelectItemMenuAction={handleGoToJurisdictionAreaView}
        suppressAggFuncInHeader
        groupSelectsChildren
        suppressRowClickSelection
        enableGroupSelection
      />
      <Dialog
        title={t('jurisdictionArea.dialog.delete.title')}
        isOpen={isLoading}
        canEscapeKeyClose={false}
        canOutsideClickClose={false}
        isCloseButtonShown={false}
      >
        <div className={Classes.DIALOG_BODY}>
          <div className="jurisdiction-area-creating-access-functions">{t('jurisdictionArea.dialog.delete.description', { accessFunctionPending })}</div>
          <ProgressBar className=".jurisdiction-area-creating-access-functions-progress" intent="primary" value={progressBarValue} />
        </div>
      </Dialog>
      <JurisdictionAreaDialog
        title={t('jurisdictionArea.dialog.deleted.title')}
        description={t('jurisdictionArea.dialog.deleted.description')}
        isJurisdictionAreaDialogOpen={isJurisdictionAreaDialogOpen}
        accessFunctionsList={failedAccessFunctions}
        handleClose={closeJurisdictionAreaDialog}
      />
    </Fragment>
  );
};

export default withRouter(connector(JurisdictionAreaList));
